import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from 'react-redux';
import { doFetch, doPost, doDelete } from '../../../redux/api/actions';
import { GET_MYTOUR, GET_TOUR_EVENTS, CREATE_TOUR_EVENT, DELETE_TOUR_EVENT } from '../../../redux/api/constants';
import { Link, useParams } from "react-router-dom";
import Layout from "../../layout";
import { useTranslation } from 'react-i18next';

function ScheduleTour() {
    const { t } = useTranslation();
    const { tourId } = useParams();
    const dispatch = useDispatch();

    const tour = useSelector(state => state.API.myTour);
    const tourEvents = useSelector(state => state.API.tourEvents) || [];
    const accessToken = useSelector(state => state.Auth.accessToken);

    const [showAddForm, setShowAddForm] = useState(false);
    const [newDate, setNewDate] = useState('');
    const [newTime, setNewTime] = useState('09:00');
    const [newCapacity, setNewCapacity] = useState('');
    const [dateError, setDateError] = useState('');

    useEffect(() => {
        if (tourId && accessToken) {
            dispatch(doFetch(GET_MYTOUR, `tourguide/tours/${tourId}`, accessToken));
            dispatch(doFetch(GET_TOUR_EVENTS, `tourguide/tours/${tourId}/events`, accessToken));
        }
    }, [tourId, accessToken, dispatch]);

    useEffect(() => {
        if (tour) {
            setNewCapacity(tour.max_tour_size || 1);
        }
    }, [tour]);

    const handleAddEvent = (e) => {
        e.preventDefault();
        setDateError('');

        if (!newDate) {
            setDateError('Please select a date');
            return;
        }

        const eventDateTime = new Date(`${newDate}T${newTime}`);
        if (eventDateTime <= new Date()) {
            setDateError('Please select a future date and time');
            return;
        }

        const params = {
            tour_date: eventDateTime.toISOString(),
            total_capacity: parseInt(newCapacity),
            quantity: 1
        };

        dispatch(doPost(CREATE_TOUR_EVENT, `tourguide/tours/${tourId}/events`, accessToken, params));
        setShowAddForm(false);
        setNewDate('');
        setNewTime('09:00');
    };

    const handleDeleteEvent = (eventId) => {
        dispatch(doDelete(DELETE_TOUR_EVENT, `tourguide/tours/${tourId}/events/${eventId}`, accessToken));
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            weekday: 'short',
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const formatTime = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getMinDate = () => {
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        return tomorrow.toISOString().split('T')[0];
    };

    const upcomingEvents = tourEvents
        .filter(event => new Date(event.tour_date) > new Date())
        .sort((a, b) => new Date(a.tour_date) - new Date(b.tour_date));

    const pastEvents = tourEvents
        .filter(event => new Date(event.tour_date) <= new Date())
        .sort((a, b) => new Date(b.tour_date) - new Date(a.tour_date));

    return (
        <Layout selected="tourguide">
            <div className="schedule-page">
                <nav aria-label="breadcrumb" className="breadcrumb-nav">
                    <ol className="breadcrumb">
                        <li className="breadcrumb-item">
                            <Link to="/">Home</Link>
                        </li>
                        <li className="breadcrumb-item">
                            <Link to="/tourguide#tab2">{t('tourguide-my-tours')}</Link>
                        </li>
                        <li className="breadcrumb-item">
                            <Link to={`/tourguide/tours/${tourId}`}>{tour?.title || 'Tour'}</Link>
                        </li>
                        <li className="breadcrumb-item active">{t('schedule-title')}</li>
                    </ol>
                </nav>

                <div className="schedule-header">
                    <div>
                        <h1>{t('schedule-title')}</h1>
                        {tour && <p className="subtitle">{tour.title}</p>}
                    </div>
                    <button
                        className="btn-primary"
                        onClick={() => setShowAddForm(!showAddForm)}
                    >
                        <i className="bi bi-plus-lg"></i> {t('schedule-add-date')}
                    </button>
                </div>

                {showAddForm && (
                    <div className="add-event-form">
                        <h3>Add New Date</h3>
                        <form onSubmit={handleAddEvent}>
                            <div className="form-row">
                                <div className="form-section">
                                    <label htmlFor="eventDate" className="form-label">Date</label>
                                    <input
                                        type="date"
                                        id="eventDate"
                                        className={`form-input ${dateError ? 'error' : ''}`}
                                        value={newDate}
                                        onChange={(e) => setNewDate(e.target.value)}
                                        min={getMinDate()}
                                    />
                                </div>
                                <div className="form-section">
                                    <label htmlFor="eventTime" className="form-label">Time</label>
                                    <input
                                        type="time"
                                        id="eventTime"
                                        className="form-input"
                                        value={newTime}
                                        onChange={(e) => setNewTime(e.target.value)}
                                    />
                                </div>
                                <div className="form-section">
                                    <label htmlFor="eventCapacity" className="form-label">{t('schedule-capacity')}</label>
                                    <input
                                        type="number"
                                        id="eventCapacity"
                                        className="form-input"
                                        value={newCapacity}
                                        onChange={(e) => setNewCapacity(e.target.value)}
                                        min="1"
                                        max={tour?.max_tour_size || 20}
                                    />
                                </div>
                            </div>
                            {dateError && <span className="error-text">{dateError}</span>}
                            <div className="form-actions">
                                <button type="submit" className="btn-primary">Add Date</button>
                                <button type="button" className="btn-secondary" onClick={() => setShowAddForm(false)}>
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                )}

                <div className="events-section">
                    <h2>Upcoming Dates</h2>
                    {upcomingEvents.length > 0 ? (
                        <div className="events-list">
                            {upcomingEvents.map((event) => (
                                <div key={event.id} className="event-card">
                                    <div className="event-date">
                                        <i className="bi bi-calendar3"></i>
                                        <span>{formatDate(event.tour_date)}</span>
                                    </div>
                                    <div className="event-time">
                                        <i className="bi bi-clock"></i>
                                        <span>{formatTime(event.tour_date)}</span>
                                    </div>
                                    <div className="event-capacity">
                                        <i className="bi bi-people"></i>
                                        <span>{event.total_booked || 0} / {event.total_capacity} booked</span>
                                    </div>
                                    <button
                                        className="btn-icon danger"
                                        onClick={() => handleDeleteEvent(event.id)}
                                        title="Delete this date"
                                    >
                                        <i className="bi bi-trash"></i>
                                    </button>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="empty-state">
                            <i className="bi bi-calendar-x"></i>
                            <p>{t('schedule-no-dates')}</p>
                        </div>
                    )}
                </div>

                {pastEvents.length > 0 && (
                    <div className="events-section past">
                        <h2>Past Dates</h2>
                        <div className="events-list">
                            {pastEvents.slice(0, 5).map((event) => (
                                <div key={event.id} className="event-card past">
                                    <div className="event-date">
                                        <i className="bi bi-calendar3"></i>
                                        <span>{formatDate(event.tour_date)}</span>
                                    </div>
                                    <div className="event-time">
                                        <i className="bi bi-clock"></i>
                                        <span>{formatTime(event.tour_date)}</span>
                                    </div>
                                    <div className="event-capacity">
                                        <i className="bi bi-people"></i>
                                        <span>{event.total_booked || 0} / {event.total_capacity} attended</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </Layout>
    );
}

export default ScheduleTour;

